import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { brandTint, TEXT_SAFE_TINT } from '~/helpers/branding/brand-backdrop'
import { tourAnchor } from '~/helpers/tour/anchors'

type IProps = {
  greeting: string
  /** Phrase de situation : par quoi reprendre, ou l'échéance qui approche. */
  focus?: string | null
  /** Le bouton vers la formation que nomme la phrase ; absent quand rien n'est à reprendre. */
  action?: { href: string, label: string } | null
  completed: number
  total: number
}

/** Circonférence du cercle de la jauge (r = 15). */
const RING = 2 * Math.PI * 15

/**
 * Bandeau d'accueil de l'espace apprenant (#74) : l'adresse à la personne, ce
 * qu'elle a terminé, et un bouton vers la formation à reprendre.
 *
 * ⚠️ Plus d'aplat de marque (#307). Le bandeau était peint de la primaire de
 * l'organisation, encre comprise : en sombre, une marque saturée y écrasait tout
 * l'écran. Il prend désormais une teinte légère de la marque sur le fond du
 * thème, `TEXT_SAFE_TINT`, dont le plafond garde le texte secondaire AA pour
 * toute primaire (épinglé dans `theme-contrast.spec.ts`). Le texte est celui du
 * thème, et la marque ne reste franche que sur le bouton et la jauge.
 */
function LearnerHero({ greeting, focus, action, completed, total }: IProps) {
  const { t } = useTranslation()
  const share = total ? completed / total : 0

  return (
    <section
      {...tourAnchor('learner.hero')}
      className="mb-8 rounded-2xl border border-border px-5 py-5 sm:px-8 sm:py-6"
      style={{ background: brandTint(TEXT_SAFE_TINT.primary) }}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl tracking-tight text-balance sm:text-3xl">
            {greeting}
          </h1>
          {focus && (
            <p className="mt-1.5 max-w-xl text-sm text-muted-foreground sm:text-base">
              {focus}
            </p>
          )}
        </div>

        {action && (
          <Button asChild className="w-full md:w-auto">
            <Link to={action.href}>{action.label}</Link>
          </Button>
        )}

        {total > 0 && (
          <div className="flex shrink-0 items-center gap-3 md:border-l md:border-border md:pl-8">
            {/* La jauge dit la même chose que le chiffre : pour un lecteur
                d'écran, la phrase entière suffit. Le trait prend la teinte de
                marque lisible, la marque brute n'étant pas garantie sur ce fond. */}
            <svg viewBox="0 0 36 36" className="size-12 -rotate-90 sm:size-14" role="img" aria-label={t('home.summary', { count: completed, total })}>
              <circle cx="18" cy="18" r="15" fill="none" strokeWidth="4" className="stroke-border" />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${share * RING} ${RING}`}
                className="stroke-primary-text transition-[stroke-dasharray] duration-500"
              />
            </svg>
            {/* Chiffre en figures PROPORTIONNELLES : `tabular-nums` fait bâiller
                un « 1 » à cette taille, et il n'y a pas de colonne à caler. */}
            <div aria-hidden="true">
              <p className="font-heading text-2xl leading-none">{`${completed} / ${total}`}</p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{t('home.summaryCaption', { count: completed })}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default LearnerHero
